import { type Node as ProsemirrorNode } from "prosemirror-model";
import { type EditorState, type Transaction } from "prosemirror-state";
import { z } from "zod/v4";

import { type LoggerInterface, getTimer } from "@moment/utils";

import { type Checksum } from "../api-types/checksum";
import { V0_MD5_CHECKSUM_VERSION, v0md5Checksum } from "../checksum/md5";
import { nodeChecksumPlugin } from "../prosemirror-plugin";
import { type Plugin } from "../prosemirror-state";

const CURRENT_CHECKSUM_VERSION = V0_MD5_CHECKSUM_VERSION;
const INVALID_CHECKSUM_HASH = "cannot_compute_invalid_doc_checksum";

export class DocumentValidator {
	constructor(
		private readonly env: string,
		private logger: LoggerInterface,
		private checksumDebuggingForceDisabled: boolean
	) {}

	checksumEnabled() {
		return this.env === "staging" || this.env === "local";
	}

	checksumDebuggingEnabled() {
		return this.checksumEnabled() && !this.checksumDebuggingForceDisabled;
	}

	/**
	 * The intent of this method is to help hydrate current editor state's direct descendant nodes with
	 * a checksum.
	 *
	 * The return value is:
	 *    a transaction that can be applied to the editor state
	 *    undefined if all the direct descendant nodes have a checksum
	 *
	 */
	createTransactionsForMissingChecksums(editorState: EditorState): Transaction | undefined {
		if (!this.checksumEnabled()) {
			return;
		}
		const timer = getTimer(this.logger);
		let checksumUpdated = false;
		const transaction = editorState.tr;
		return timer.timeFunction("create_transactions_for_missing_checksums", () => {
			editorState.doc.descendants((node, pos) => {
				const version = node.attrs["checksum_version"];
				const hash = node.attrs["checksum_hash"];
				// Inspect the current doc's node attrs.
				let newAttrs = { ...node.attrs };
				const recomputeChecksum = !hash || version !== CURRENT_CHECKSUM_VERSION;
				if (recomputeChecksum) {
					checksumUpdated = true;
					const result = this.checksum(node);
					newAttrs = {
						...newAttrs,
						checksum_hash: result.hash,
						checksum_version: result.version,
					};
				}

				// Update the node attrs if needed
				if (checksumUpdated) {
					transaction.setNodeMarkup(pos, undefined, newAttrs);
				}
				return false;
			});
			return checksumUpdated ? transaction : undefined;
		});
	}

	// Used by external consumers to compute a checksum for a given node
	// Currently only intended to be used in a prosemirror plugin
	// Intended to be used agnostically across any node.
	// Mainly used for computing checksums for direct descendant nodes of the parent document node.
	checksum(node: ProsemirrorNode): Checksum {
		const result = this.parseNode(node);
		let serializedParsedNodes: string = "{}";
		try {
			serializedParsedNodes = JSON.stringify(result);
		} catch (e) {
			this.logger.error("Failed to stringify parsed nodes for checksum", {
				error: e,
				nodeType: node.type.name,
				nodeText: node.textContent,
			});
		}
		const checksumResult = v0md5Checksum(serializedParsedNodes);
		if (this.checksumDebuggingEnabled()) {
			checksumResult.extras = {
				debug: result,
			};
		}
		return checksumResult;
	}

	/**
	 * Returns a list of conflicts for the document checksums.
	 * If empty, the check passed.
	 */
	checksumResultCompare(
		document: Checksum | undefined,
		inboundRequest: Checksum | undefined
	): unknown[] {
		if (!inboundRequest || !document) {
			return [];
		}
		const isValid =
			document.version === inboundRequest.version && document.hash === inboundRequest.hash;
		if (isValid) {
			return [];
		}

		return this.getConflicts(this.logger, document, inboundRequest);
	}

	checksumDocument(editorState: EditorState): Checksum | undefined {
		if (!this.checksumEnabled()) {
			return;
		}
		const timer = getTimer(this.logger);
		return timer.timeFunction("checksum_document", () => {
			let foundInvalidChecksum = false;
			const hashes: string[] = [];
			const debug: unknown[] = [];
			// Aggregate all the direct node's hashes and hash that.
			editorState.doc.descendants((node, _pos) => {
				const version = node.attrs["checksum_version"];
				const hash = node.attrs["checksum_hash"];
				let resolvedHash: string;
				if (version === undefined || hash === undefined) {
					let checksumParsedNode: unknown | undefined = undefined;
					if (this.checksumDebuggingEnabled()) {
						checksumParsedNode = this.parseNode(node);
					}
					this.logger.error("found undefined checksum in descendant node", {
						type: node.type.name,
						childrenCount: editorState.doc.childCount,
						version,
						hash,
						localParsedNodeResult: checksumParsedNode,
					});
					foundInvalidChecksum = true;
					resolvedHash = "undefined_checksum_found";
				} else if (version !== CURRENT_CHECKSUM_VERSION) {
					this.logger.error("found unsupported checksum version", {
						type: node.type.name,
						childrenCount: editorState.doc.childCount,
					});
					foundInvalidChecksum = true;
					resolvedHash = "unsupported_checksum_version";
				} else {
					resolvedHash = hash;
				}
				hashes.push(resolvedHash);

				if (this.checksumDebuggingEnabled()) {
					debug.push({
						hash: resolvedHash,
						version,
						info: this.parseNode(node),
					});
				}
				return false;
			});

			// If there exists an invalid checksum somewhere, early exit.
			if (foundInvalidChecksum) {
				const result: Checksum = {
					// Unsure the version but this is the latest version that we are using.
					version: CURRENT_CHECKSUM_VERSION,
					hash: INVALID_CHECKSUM_HASH,
				};
				if (this.checksumDebuggingEnabled()) {
					result.extras = {
						debug,
					};
				}
				return result;
			}
			// Stringify the array of hashes and perform a checksum
			const serializedHashes = JSON.stringify(hashes);
			const checksumResult = v0md5Checksum(serializedHashes);
			const result: Checksum = {
				// Version might change in the future. Explicitly returning here as it would be less confusing
				// for when that happens.
				version: checksumResult.version,
				hash: checksumResult.hash,
			};
			if (this.checksumDebuggingEnabled()) {
				result.extras = {
					debug,
				};
			}
			return result;
		});
	}

	nodeChecksumPlugin(): Plugin | undefined {
		if (!this.checksumEnabled()) {
			return;
		}
		return nodeChecksumPlugin(this.logger, this);
	}

	private getConflicts(
		logger: LoggerInterface,
		document: Checksum,
		inboundRequest: Checksum
	): unknown[] {
		const documentNodeParseResult = document.extras?.debug ?? [];
		const inboundNodeParseResult = inboundRequest.extras?.debug ?? [];
		const conflicts: unknown[] = [];
		if (documentNodeParseResult.length != inboundNodeParseResult.length) {
			logger.debug("[checksum] resulting list sizes are different", {
				document: document.extras?.debug,
				inboundRequest: inboundRequest.extras?.debug,
			});
		}
		for (
			let i = 0;
			i < documentNodeParseResult.length && i < inboundNodeParseResult.length;
			i++
		) {
			// This is checking the checksum result values. If the hashed values differ,
			// there was a conflict.
			// @ts-ignore
			const documentNodeParseResultHash = documentNodeParseResult[i]?.["hash"];
			if (
				documentNodeParseResultHash !== INVALID_CHECKSUM_HASH &&
				// @ts-ignore
				documentNodeParseResultHash !== inboundNodeParseResult[i]?.["hash"]
			) {
				conflicts.push({
					document: documentNodeParseResult[i],
					inbound: inboundNodeParseResult[i],
					index: i,
				});
			}
		}
		if (conflicts.length !== 0 && this.checksumDebuggingEnabled()) {
			logger.debug("[checksum] found conflicts", {
				conflicts,
			});
		}
		return conflicts;
	}

	// Used for validating the structure of the tree given a node.
	// Helpful for getting debugging information from prosemirror nodes.
	parseNode(prosemirrorNode: ProsemirrorNode): NodeParseResult[] {
		// Add the parent node to the results.
		const checksumResults: NodeParseResult[] = [
			newNodeParseResult(
				prosemirrorNode.type.name,
				prosemirrorNode.text,
				0,
				prosemirrorNode.nodeSize
			),
		];
		// Recursively search the children of the parent node.
		prosemirrorNode.descendants((node, pos, _parent, _index) => {
			// pos is relative to the parent `prosemirrorNode`.
			checksumResults.push(
				newNodeParseResult(node.type.name, node.text, pos, pos + node.nodeSize)
			);

			// Our intent is to iterate through all the child nodes; we should recurse through by return true.
			// Note: returning false will avoid traversing deeper down the tree.
			return true;
		});
		return checksumResults;
	}
}

// The "node" structure used for computing the checksum.
const nodeParseResult = z.object({
	type: z.string(),
	start: z.number().optional(),
	end: z.number().optional(),
	content: z.string().optional(),
});

export type NodeParseResult = z.infer<typeof nodeParseResult>;

// Ensures consistent ordering of keys.
const newNodeParseResult = (
	type: string,
	content: string | undefined,
	start: number,
	end: number
): NodeParseResult => {
	const result: NodeParseResult = {
		type,
		start,
		end,
	};
	if (content) {
		result.content = content;
	}
	return result;
};
